import {
  Alert,
  Anchor,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconArrowRight,
  IconBrain,
  IconBulb,
  IconChevronDown,
  IconExternalLink,
  IconHelpCircle,
  IconSparkles,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  getZendeskDraft,
  patchZendeskDraft,
  proposeZendeskDraft,
  type ZendeskDraftDetail,
  type ZendeskDraftSummary,
  type ZendeskEditReason,
  type ZendeskProposeResult,
  type ZendeskReviewOutcome,
  type ZendeskReviewStatus,
} from '@mygpt/app/apis/services/zendesk';
import {
  REPLY_RULE_KINDS,
  createReplyRule,
  getReplyRuleSettings,
  proposeReplyRules,
  type ReplyRuleKind,
  type ReplyRuleProposal,
} from '@mygpt/app/apis/services/replyRules';
import { errorDetail, errorPayload, errorStatus, unitStatusColor } from '@/components/Datasets/KnowledgeLoop/shared';
import {
  ACCENT,
  EDIT_REASONS,
  charDiff,
  formatDateTime,
  formatSimilarity,
  isKnowledgeGap,
  needsReview,
  reasonLabel,
  reviewColor,
  reviewDisplay,
  statusColor,
  statusLabel,
} from './shared';

interface Props {
  robotId: string;
  datasetId?: string | null; // 沉淀先ナレッジベース（ナレッジループ画面への直リンク用）
  draft: ZendeskDraftSummary | null;
  reviewerName?: string;
  onClose: () => void;
  onChanged: (patch: Partial<ZendeskDraftSummary>) => void;
}

// 「不需要改进 ▾」の選択肢 = 提案以外の改善結果（選んだ時点でタスク完了）
const DISMISS_OUTCOMES: ZendeskReviewOutcome[] = ['wording_only', 'case_specific', 'duplicate', 'no_action'];
const GAP_DISMISS_OUTCOMES: ZendeskReviewOutcome[] = ['gap_logged', 'no_action'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const Panel = ({ title, children, tone }: { title: string; children: React.ReactNode; tone?: 'draft' | 'final' }) => (
  <div
    style={{
      border: '1px solid #E9ECEF',
      borderRadius: 10,
      overflow: 'hidden',
      background: '#fff',
    }}
  >
    <div
      style={{
        padding: '7px 12px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        color: tone === 'final' ? '#0B7285' : '#868E96',
        background: tone === 'final' ? 'rgba(11,114,133,0.06)' : '#F8F9FA',
        borderBottom: '1px solid #E9ECEF',
      }}
    >
      {title}
    </div>
    <div style={{ padding: 12, fontSize: 13, lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children}
    </div>
  </div>
);

const DiffText = ({ segs }: { segs: { text: string; type: 'equal' | 'del' | 'ins' }[] }) => (
  <>
    {segs.map((s, i) =>
      s.type === 'equal' ? (
        <span key={i}>{s.text}</span>
      ) : s.type === 'del' ? (
        <del
          key={i}
          style={{ background: 'rgba(250,82,82,0.14)', color: '#C92A2A', textDecorationColor: 'rgba(201,42,42,0.5)' }}
        >
          {s.text}
        </del>
      ) : (
        <ins key={i} style={{ background: 'rgba(12,166,120,0.16)', color: '#087F5B', textDecoration: 'none' }}>
          {s.text}
        </ins>
      )
    )}
  </>
);

const clip = (text: string, max = 700) => (text.length > max ? `${text.slice(0, max)}…` : text);

export const DetailDrawer = ({ robotId, datasetId, draft, reviewerName, onClose, onChanged }: Props) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<ZendeskDraftDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [result, setResult] = useState<ZendeskProposeResult | null>(null);
  // bot 級「回答ルール」：この bot でスイッチ ON のときだけ「ルールにする」を出す
  const [rulesEnabled, setRulesEnabled] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleLoading, setRuleLoading] = useState(false);
  const [ruleDrafts, setRuleDrafts] = useState<(ReplyRuleProposal & { saved?: boolean })[]>([]);
  const [ruleSaving, setRuleSaving] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    if (!draft?.id) return undefined;
    getReplyRuleSettings(robotId)
      .then(({ data }) => alive && setRulesEnabled(!!data.enabled))
      .catch(() => alive && setRulesEnabled(false));
    return () => {
      alive = false;
    };
  }, [robotId, draft?.id]);

  const openRuleModal = async () => {
    if (!detail) return;
    setRuleOpen(true);
    setRuleLoading(true);
    setRuleDrafts([]);
    try {
      const { data } = await proposeReplyRules(robotId, detail.id);
      setRuleDrafts((data.proposals || []).map((p) => ({ ...p })));
    } catch (e) {
      console.error('propose reply rules failed:', e);
    } finally {
      setRuleLoading(false);
    }
  };

  const saveRule = async (index: number) => {
    const d = ruleDrafts[index];
    if (!d || d.text.trim().length < 4) return;
    setRuleSaving(index);
    try {
      await createReplyRule(robotId, { kind: d.kind, text: d.text.trim() });
      setRuleDrafts((list) => list.map((x, i) => (i === index ? { ...x, saved: true } : x)));
      showNotification({ color: 'green', message: t('zendesk_ops.rules.rule_saved') });
    } catch (e: any) {
      // 数値・日付・個人情報を含むルールは 400
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setRuleSaving(null);
    }
  };

  useEffect(() => {
    if (!draft) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setResult(null);
    getZendeskDraft(robotId, draft.id)
      .then(({ data }) => {
        if (!cancelled) setDetail(data);
      })
      .catch((e) => console.error('load draft detail failed:', e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [robotId, draft?.id]);

  const diff = useMemo(() => {
    if (!detail?.draft_text || !detail?.final_text) return null;
    if (detail.status === 'answered_as_is') return null;
    return charDiff(detail.draft_text, detail.final_text);
  }, [detail?.draft_text, detail?.final_text, detail?.status]);

  const apply = (patch: Partial<ZendeskDraftSummary>) => {
    setDetail((d) => (d ? { ...d, ...patch } : d));
    onChanged(patch);
  };

  const setReason = async (reason: ZendeskEditReason) => {
    if (!detail) return;
    const next = detail.edit_reason === reason ? '' : reason;
    setSaving('reason');
    try {
      const { data } = await patchZendeskDraft(robotId, detail.id, { edit_reason: next });
      apply({ edit_reason: (data.edit_reason as ZendeskEditReason) || null });
    } catch (e) {
      console.error('save edit_reason failed:', e);
    } finally {
      setSaving(null);
    }
  };

  const setReview = async (review_status: ZendeskReviewStatus) => {
    if (!detail) return;
    setSaving('review');
    try {
      const { data } = await patchZendeskDraft(robotId, detail.id, {
        review_status,
        reviewed_by: review_status === 'pending' ? '' : reviewerName || '',
      });
      apply({
        review_status: data.review_status,
        reviewed_by: data.reviewed_by,
        reviewed_at: new Date().toISOString(),
      });
      showNotification({ message: t('zendesk_ops.save_success'), color: 'green' });
    } catch (e) {
      console.error('save review_status failed:', e);
    } finally {
      setSaving(null);
    }
  };

  // 「不需要改进」：1 クリックで 完了 + 改善結果 を書く（3 状態は保ちつつ人の操作は減らす）
  const dismiss = async (outcome: ZendeskReviewOutcome) => {
    if (!detail) return;
    setSaving('dismiss');
    try {
      const { data } = await patchZendeskDraft(robotId, detail.id, {
        review_status: 'done',
        reviewed_by: reviewerName || '',
        review_outcome: outcome,
      });
      apply({
        review_status: data.review_status,
        reviewed_by: data.reviewed_by,
        reviewed_at: new Date().toISOString(),
        review_outcome: data.review_outcome || outcome,
      });
      showNotification({ message: t('zendesk_ops.outcome_saved'), color: 'green' });
      // 「仅措辞」で閉じた直し方は bot の回答ルールの材料：スイッチ ON なら提案を出す
      if (outcome === 'wording_only' && rulesEnabled && detail.final_text) openRuleModal();
    } catch (e) {
      console.error('dismiss failed:', e);
    } finally {
      setSaving(null);
    }
  };

  // 「ナレッジ改善を提案」：毎日のナレッジ抽出と同じ判定で台帳へ（直接公開はしない）
  const propose = async () => {
    if (!detail) return;
    setSaving('propose');
    try {
      const { data } = await proposeZendeskDraft(robotId, detail.id, datasetId ? { dataset_id: datasetId } : {});
      setResult(data);
      // 提案した時点で改善タスクは完了（サーバ側も同じく done にする）
      apply({
        knowledge_unit_id: data.unit_id,
        knowledge_unit_status: data.unit?.status || null,
        review_outcome: data.unit_id ? 'proposed' : 'no_action',
        review_status: 'done',
        reviewed_by: reviewerName || null,
        reviewed_at: new Date().toISOString(),
      });
    } catch (e: any) {
      const status = errorStatus(e);
      const payload = errorPayload(e);
      const reason: string = typeof payload === 'string' ? payload : '';
      let message = reason || t('zendesk_ops.load_error');
      if (status === 409 && reason.startsWith('Already proposed')) message = t('zendesk_ops.propose.already');
      else if (status === 409 && reason.startsWith('Distillation is running'))
        message = t('zendesk_ops.propose.running');
      else if (status === 409 && reason.includes('daily distillation')) message = t('zendesk_ops.propose.no_proposal');
      else if (status === 400 && reason.startsWith('No agent reply')) message = t('zendesk_ops.propose.no_reply');
      else if (status === 400 && reason.startsWith('No sink dataset')) message = t('zendesk_ops.propose.no_dataset');
      showNotification({ message, color: status === 409 ? 'yellow' : 'red' });
      setProposeOpen(false);
    } finally {
      setSaving(null);
    }
  };

  const d = detail || draft;
  const rv = d ? reviewDisplay(d) : 'out_of_scope';
  const reviewable = d ? needsReview(d.status, d.skip_reason) : false;
  const isGap = !!d && d.status === 'skipped' && isKnowledgeGap(d.skip_reason);
  const unitDataset = result?.dataset_id || datasetId;
  const queueLink = (unitId?: string | null) =>
    unitDataset
      ? `/bot/${robotId}/datasets/${unitDataset}/data-source/zendesk?tab=queue${unitId ? `&unit=${unitId}` : ''}`
      : null;
  const gapsLink = datasetId ? `/bot/${robotId}/datasets/${datasetId}/data-source/zendesk?tab=gaps` : null;
  const dismissOptions = isGap ? GAP_DISMISS_OUTCOMES : DISMISS_OUTCOMES;
  const taskDone = !!d && d.review_status === 'done';
  const citedLabel = (c: { type: string; id: string; title?: string }) => {
    if (c.type === 'url') return c.id;
    // rag_retrieve の FAQ 命中は「Internal Knowledge」で id を持たない → 業務名で見せる
    if (c.title === 'Internal Knowledge' || !UUID_RE.test(c.id)) return t('zendesk_ops.drawer.cited_internal');
    return c.title || c.id;
  };

  const resultText = (r: ZendeskProposeResult) => {
    if (r.reused) return t('zendesk_ops.propose.result_reused');
    return t(`zendesk_ops.propose.result_${r.decision}`);
  };

  return (
    <Drawer
      opened={!!draft}
      onClose={onClose}
      position="right"
      size={760}
      padding="lg"
      title={
        d ? (
          <Group gap={8} wrap="nowrap">
            <Text fw={700} fz={15} style={{ fontFamily: 'monospace' }}>
              #{d.ticket_id}
            </Text>
            {d.ticket_url && (
              <Anchor
                href={d.ticket_url}
                target="_blank"
                rel="noreferrer"
                c="gray.5"
                display="inline-flex"
                title={t('zendesk_ops.drawer.open_zendesk')}
              >
                <IconExternalLink size={14} />
              </Anchor>
            )}
            <Text fw={600} fz={14} truncate style={{ maxWidth: 440 }}>
              {d.ticket_subject || t('zendesk_ops.drawer.title')}
            </Text>
          </Group>
        ) : (
          t('zendesk_ops.drawer.title')
        )
      }
    >
      {!d ? null : (
        <Stack gap={14}>
          <Group gap={8} wrap="wrap">
            <Badge variant="light" color={statusColor(d.status)} radius="sm" style={{ textTransform: 'none' }}>
              {statusLabel(t, d.status, d.skip_reason)}
            </Badge>
            {d.similarity !== null && d.similarity !== undefined && (
              <Badge variant="outline" color="gray" radius="sm" style={{ textTransform: 'none' }}>
                {t('zendesk_ops.col.similarity')} {formatSimilarity(d.similarity)}
              </Badge>
            )}
            <Text fz={12} c="gray.6">
              {t('zendesk_ops.drawer.version', { version: d.draft_version })} · {t('zendesk_ops.drawer.posted_at')}{' '}
              {formatDateTime(d.created_at)}
              {d.answered_at ? ` · ${t('zendesk_ops.drawer.answered_at')} ${formatDateTime(d.answered_at)}` : ''}
              {d.final_author_name ? ` · ${t('zendesk_ops.drawer.assignee')} ${d.final_author_name}` : ''}
            </Text>
            {reviewable && (
              <Group gap={6} wrap="nowrap">
                <Badge variant="dot" color={reviewColor(rv)} radius="sm" size="sm" style={{ textTransform: 'none' }}>
                  {t(`zendesk_ops.review.${rv}`)}
                  {d.reviewed_by ? ` · ${d.reviewed_by}` : ''}
                </Badge>
                {taskDone && !d.knowledge_unit_id && (
                  <Anchor fz={11.5} c="gray.6" onClick={() => saving !== 'review' && setReview('pending')}>
                    {t('zendesk_ops.drawer.reopen')}
                  </Anchor>
                )}
              </Group>
            )}
          </Group>

          {loading && !detail && (
            <Group justify="center" py={30}>
              <Loader size="sm" />
            </Group>
          )}

          {detail && (
            <>
              {detail.question && (
                <Panel title={t('zendesk_ops.drawer.question')}>
                  <Text fz={13} c="gray.7" style={{ whiteSpace: 'pre-wrap' }}>
                    {detail.question}
                  </Text>
                </Panel>
              )}
              {isGap ? (
                <Alert
                  color="orange"
                  variant="light"
                  icon={<IconHelpCircle size={16} />}
                  title={t('zendesk_ops.gap_block.title')}
                >
                  <Text fz={12.5}>
                    {t(
                      `zendesk_ops.skip_detail.${detail.skip_detail || 'unknown'}`,
                      t('zendesk_ops.skip_detail.unknown')
                    )}
                  </Text>
                  <Text fz={12} c="gray.6" mt={4}>
                    {t('zendesk_ops.gap_block.desc')}
                  </Text>
                </Alert>
              ) : (
                detail.status === 'skipped' && (
                  <Text fz={12.5} c="gray.6">
                    {t('zendesk_ops.drawer.skip_reason')}：
                    {detail.skip_reason ? t(`zendesk_ops.skip.${detail.skip_reason}`) : '—'}
                  </Text>
                )
              )}
              {detail.last_error && !isGap && (
                <Text fz={12} c="red.6">
                  {detail.last_error}
                </Text>
              )}
              {(detail.draft_text || detail.final_text) && (
                <div style={{ display: 'grid', gridTemplateColumns: detail.final_text ? '1fr 1fr' : '1fr', gap: 12 }}>
                  {detail.draft_text && (
                    <Panel
                      title={isGap ? t('zendesk_ops.drawer.ai_answer_unsent') : t('zendesk_ops.drawer.draft')}
                      tone="draft"
                    >
                      {diff ? <DiffText segs={diff.draft} /> : detail.draft_text}
                    </Panel>
                  )}
                  {detail.final_text ? (
                    <Panel title={t('zendesk_ops.drawer.final')} tone="final">
                      {diff ? <DiffText segs={diff.final} /> : detail.final_text}
                    </Panel>
                  ) : (
                    detail.status !== 'skipped' &&
                    detail.status !== 'failed' && (
                      <Text fz={12.5} c="gray.5">
                        {t('zendesk_ops.drawer.no_final')}
                      </Text>
                    )
                  )}
                </div>
              )}

              {detail.status === 'dry_run' && (
                <Alert color="grape" variant="light" p="xs">
                  <Text fz={12.5}>{t('zendesk_ops.drawer.dry_run_note')}</Text>
                </Alert>
              )}
              {detail.cited_refs &&
                ((detail.cited_refs.citations || []).length > 0 ||
                  !!detail.cited_refs.faq_id ||
                  (detail.cited_refs.unit_ids || []).length > 0) && (
                  <Stack gap={4}>
                    <Text fz={12.5} fw={700} c="gray.7">
                      {t('zendesk_ops.drawer.cited_title')}
                    </Text>
                    <Group gap={6}>
                      {(detail.cited_refs.unit_ids || []).map((u) => {
                        const badge = (
                          <Badge variant="light" color="teal" radius="sm" style={{ textTransform: 'none' }}>
                            {t('zendesk_ops.drawer.cited_unit', { version: u.version || '—' })}
                            {u.title ? ` · ${u.title}` : ''}
                          </Badge>
                        );
                        const link = queueLink(u.id);
                        return link ? (
                          <Link key={u.id} to={link} style={{ textDecoration: 'none' }}>
                            {badge}
                          </Link>
                        ) : (
                          <span key={u.id}>{badge}</span>
                        );
                      })}
                      {detail.cited_refs.faq_id && !(detail.cited_refs.unit_ids || []).length && (
                        <Badge variant="light" color="gray" radius="sm" style={{ textTransform: 'none' }}>
                          {t('zendesk_ops.drawer.cited_faq')}
                        </Badge>
                      )}
                      {(detail.cited_refs.citations || []).map((c, i) => (
                        <Badge
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${c.id}-${i}`}
                          variant="outline"
                          color="gray"
                          radius="sm"
                          style={{ textTransform: 'none', maxWidth: 320 }}
                        >
                          {citedLabel(c)}
                          {c.page && c.page !== '0' ? ` p.${c.page}` : ''}
                        </Badge>
                      ))}
                    </Group>
                  </Stack>
                )}
              {!!detail.cited_rules?.length && (
                <Stack gap={4}>
                  <Text fz={12.5} fw={700} c="gray.7">
                    {t('zendesk_ops.rules.cited_title')}
                  </Text>
                  <Group gap={6}>
                    {detail.cited_rules.map((r) => (
                      <Badge
                        key={r.id}
                        variant="light"
                        color="violet"
                        radius="sm"
                        leftSection={<IconBrain size={11} />}
                        style={{ textTransform: 'none', maxWidth: 360 }}
                      >
                        {t(`zendesk_ops.rules.kind.${r.kind}`)} · {r.text}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              )}

              {reviewable && !isGap && (
                <div>
                  <Group gap={8} mb={8} align="center">
                    <Text fz={12.5} fw={700} c="gray.7">
                      {t('zendesk_ops.drawer.reason_label')}
                    </Text>
                    {detail.edit_reason_ai && (
                      <Group gap={4}>
                        <IconSparkles size={12} color="#7C68FB" />
                        <Text fz={11.5} c="violet.6">
                          {t('zendesk_ops.drawer.ai_suggested', { reason: reasonLabel(t, detail.edit_reason_ai) })}
                        </Text>
                      </Group>
                    )}
                  </Group>
                  <Group gap={6}>
                    {EDIT_REASONS.map((r) => {
                      const selected = detail.edit_reason === r;
                      const aiHint = !detail.edit_reason && detail.edit_reason_ai === r;
                      return (
                        <button
                          key={r}
                          type="button"
                          disabled={saving === 'reason'}
                          onClick={() => setReason(r)}
                          style={{
                            border: `1px solid ${selected ? ACCENT : aiHint ? '#B197FC' : '#E3E6EB'}`,
                            borderStyle: aiHint && !selected ? 'dashed' : 'solid',
                            background: selected ? ACCENT : '#fff',
                            color: selected ? '#fff' : '#495057',
                            borderRadius: 999,
                            padding: '5px 12px',
                            fontSize: 12.5,
                            cursor: 'pointer',
                          }}
                        >
                          {t(`zendesk_ops.reason.${r}`)}
                        </button>
                      );
                    })}
                  </Group>
                </div>
              )}

              {/* 底栏：状態ごとに主動作 1 つ + 「不需要改进 ▾」だけ。処理状態と担当は上のメタ行に */}
              {reviewable && (
                <Group gap={8} mt={4} wrap="wrap">
                  {detail.knowledge_unit_id ? (
                    <>
                      <Badge
                        variant="light"
                        color={detail.knowledge_unit_status ? unitStatusColor(detail.knowledge_unit_status) : 'teal'}
                        radius="sm"
                        leftSection={<IconBulb size={12} />}
                        style={{ textTransform: 'none' }}
                      >
                        {t('zendesk_ops.drawer.proposed')}
                        {detail.knowledge_unit_status
                          ? ` · ${t(`zendesk_ops.unit_status.${detail.knowledge_unit_status}`)}`
                          : ''}
                      </Badge>
                      {queueLink(detail.knowledge_unit_id) && (
                        <Button
                          size="xs"
                          variant="subtle"
                          component={Link}
                          to={queueLink(detail.knowledge_unit_id) as string}
                          rightSection={<IconArrowRight size={14} />}
                        >
                          {t('zendesk_ops.drawer.open_unit')}
                        </Button>
                      )}
                    </>
                  ) : taskDone ? (
                    <Badge variant="light" color="gray" radius="sm" style={{ textTransform: 'none' }}>
                      {detail.review_outcome
                        ? t(`zendesk_ops.outcome.${detail.review_outcome}`)
                        : t('zendesk_ops.review.done')}
                    </Badge>
                  ) : (
                    <>
                      {isGap
                        ? gapsLink && (
                            <Button
                              size="xs"
                              color="orange"
                              component={Link}
                              to={gapsLink}
                              rightSection={<IconArrowRight size={14} />}
                            >
                              {t('zendesk_ops.gap_block.open_gaps')}
                            </Button>
                          )
                        : detail.final_text && (
                            <Button
                              size="xs"
                              color="teal"
                              leftSection={<IconBulb size={14} />}
                              loading={saving === 'propose'}
                              onClick={() => setProposeOpen(true)}
                            >
                              {t('zendesk_ops.drawer.propose')}
                            </Button>
                          )}
                      <Menu shadow="md" width={220} position="bottom-start">
                        <Menu.Target>
                          <Button
                            size="xs"
                            variant="default"
                            loading={saving === 'dismiss'}
                            rightSection={<IconChevronDown size={14} />}
                          >
                            {t('zendesk_ops.drawer.no_improvement')}
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {dismissOptions.map((o) => (
                            <Menu.Item key={o} onClick={() => dismiss(o)}>
                              {t(`zendesk_ops.outcome.${o}`)}
                            </Menu.Item>
                          ))}
                        </Menu.Dropdown>
                      </Menu>
                    </>
                  )}
                  {!detail.knowledge_unit_id && detail.faq_id && (
                    <Badge variant="outline" color="gray" radius="sm" style={{ textTransform: 'none' }}>
                      {t('zendesk_ops.drawer.faq_legacy')}
                    </Badge>
                  )}
                </Group>
              )}
              {rulesEnabled &&
                !!detail.final_text &&
                (detail.status === 'answered_edited' || detail.status === 'answered_self') && (
                  <Group gap={8}>
                    <Button
                      size="xs"
                      variant="light"
                      color="violet"
                      leftSection={<IconBrain size={14} />}
                      onClick={openRuleModal}
                    >
                      {t('zendesk_ops.rules.make_rule')}
                    </Button>
                  </Group>
                )}
            </>
          )}
        </Stack>
      )}

      <Modal
        opened={ruleOpen}
        onClose={() => ruleSaving === null && setRuleOpen(false)}
        centered
        size="lg"
        title={t('zendesk_ops.rules.make_rule')}
      >
        <Stack gap="sm">
          {ruleLoading ? (
            <Group gap={8} py="md" justify="center">
              <Loader size="xs" />
              <Text fz={12.5} c="gray.6">
                {t('zendesk_ops.rules.proposals_loading')}
              </Text>
            </Group>
          ) : (
            <>
              {ruleDrafts.length === 0 && (
                <Text fz={12.5} c="gray.6">
                  {t('zendesk_ops.rules.no_proposal')}
                </Text>
              )}
              {ruleDrafts.map((d, i) => (
                <Stack key={`rule-${i}`} gap={6} style={{ border: '1px solid #E9ECEF', borderRadius: 10, padding: 10 }}>
                  <Group gap={8} align="flex-end" wrap="nowrap">
                    <Select
                      size="xs"
                      w={160}
                      allowDeselect={false}
                      disabled={!!d.saved}
                      data={REPLY_RULE_KINDS.map((k) => ({ value: k, label: t(`zendesk_ops.rules.kind.${k}`) }))}
                      value={d.kind}
                      onChange={(v) =>
                        v &&
                        setRuleDrafts((list) => list.map((x, j) => (j === i ? { ...x, kind: v as ReplyRuleKind } : x)))
                      }
                    />
                    <Textarea
                      size="xs"
                      autosize
                      minRows={1}
                      maxLength={300}
                      disabled={!!d.saved}
                      style={{ flex: 1 }}
                      value={d.text}
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        setRuleDrafts((list) => list.map((x, j) => (j === i ? { ...x, text: v } : x)));
                      }}
                    />
                    <Button
                      size="xs"
                      color="violet"
                      variant={d.saved ? 'light' : 'filled'}
                      disabled={!!d.saved || d.text.trim().length < 4}
                      loading={ruleSaving === i}
                      onClick={() => saveRule(i)}
                    >
                      {d.saved ? t('knowledge_loop.chat.saved') : t('zendesk_ops.rules.save_rule')}
                    </Button>
                  </Group>
                  {d.why && (
                    <Text fz={11.5} c="gray.6">
                      {t('zendesk_ops.rules.why')}：{d.why}
                    </Text>
                  )}
                </Stack>
              ))}
              <Group justify="space-between">
                <Text fz={11.5} c="gray.5">
                  {t('zendesk_ops.rules.no_facts_note')}
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setRuleDrafts((list) => [...list, { kind: 'style_rule', text: '' }])}
                >
                  {t('zendesk_ops.rules.add_blank')}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={proposeOpen}
        onClose={() => {
          if (saving === 'propose') return;
          setProposeOpen(false);
        }}
        centered
        size="lg"
        title={t('zendesk_ops.propose.title')}
      >
        {detail && (
          <Stack gap="sm">
            {result ? (
              <>
                <Alert
                  color={result.decision === 'skipped' ? 'gray' : result.decision === 'conflict' ? 'orange' : 'teal'}
                  variant="light"
                  icon={<IconBulb size={16} />}
                >
                  <Text fz={13}>{resultText(result)}</Text>
                  {result.decision === 'skipped' && result.skip_samples?.[0]?.reason && (
                    <Text fz={12} c="gray.6" mt={4}>
                      {t('zendesk_ops.propose.skip_reason', { reason: result.skip_samples[0].reason })}
                    </Text>
                  )}
                  {result.unit?.title && (
                    <Text fz={12.5} fw={600} mt={6}>
                      {result.unit.title}
                    </Text>
                  )}
                </Alert>
                <Group justify="flex-end" gap={8}>
                  <Button size="xs" variant="default" onClick={() => setProposeOpen(false)}>
                    {t('zendesk_ops.propose.close')}
                  </Button>
                  {result.unit_id && queueLink(result.unit_id) && (
                    <Button
                      size="xs"
                      component={Link}
                      to={queueLink(result.unit_id) as string}
                      rightSection={<IconArrowRight size={14} />}
                    >
                      {t('zendesk_ops.drawer.open_unit')}
                    </Button>
                  )}
                </Group>
              </>
            ) : (
              <>
                <Text fz={12.5} c="gray.7">
                  {t('zendesk_ops.propose.desc')}
                </Text>
                {detail.question && (
                  <Panel title={t('zendesk_ops.propose.question')}>{clip(detail.question, 400)}</Panel>
                )}
                <Panel title={t('zendesk_ops.propose.answer')} tone="final">
                  {clip(detail.final_text || '')}
                </Panel>
                {!datasetId && (
                  <Text fz={12} c="gray.5">
                    {t('zendesk_ops.propose.dataset_auto')}
                  </Text>
                )}
                <Group justify="flex-end" gap={8}>
                  <Button
                    size="xs"
                    variant="default"
                    disabled={saving === 'propose'}
                    onClick={() => setProposeOpen(false)}
                  >
                    {t('zendesk_ops.propose.cancel')}
                  </Button>
                  <Button size="xs" color="teal" loading={saving === 'propose'} onClick={propose}>
                    {t('zendesk_ops.propose.confirm')}
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        )}
      </Modal>
    </Drawer>
  );
};
